import { Lesson } from "../../types/education";

// Mock lesson data based on lessonToTopicMapping in curriculumService.ts
// This provides sample content for testing the education hub infrastructure
export const mockLessons: Lesson[] = [
  {
    id: "color-basics",
    title: "Color Basics Introduction",
    description:
      "Learn the fundamentals of color theory, including the color wheel, primary colors, and basic color relationships.",
    duration: "45",
    difficulty: "beginner",
    category: "Foundations",
    completed: false,
    locked: false,
    progress: 0,
    objectives: [
      "Understand the color wheel structure",
      "Identify primary, secondary, and tertiary colors",
      "Learn basic color terminology",
      "Explore color relationships",
    ],
    outcomes: [
      "Ability to identify and name colors",
      "Understanding of basic color mixing",
      "Foundation for advanced color theory",
    ],
  },
  {
    id: "color-wheel",
    title: "Color Wheel Fundamentals",
    description:
      "Deep dive into the color wheel, its history, and how to use it as a tool for creating harmonious color schemes.",
    duration: "60",
    difficulty: "beginner",
    category: "Foundations",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["color-basics"],
    objectives: [
      "Master color wheel navigation",
      "Understand color temperature",
      "Learn about color value and saturation",
      "Practice color identification",
    ],
    outcomes: [
      "Proficient use of color wheel",
      "Understanding of warm vs cool colors",
      "Ability to create basic color schemes",
    ],
  },
  {
    id: "understanding-color-harmony",
    title: "Understanding Color Harmony",
    description:
      "Learn what color harmony means, identify main types of harmonies, and apply basic harmony principles to create visual balance.",
    duration: "50",
    difficulty: "beginner",
    category: "Harmony",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["color-wheel"],
    objectives: [
      "Define color harmony and its importance",
      "Identify complementary, analogous, and triadic harmonies",
      "Apply harmony principles to simple designs",
      "Recognize harmonious combinations in real examples",
    ],
    outcomes: [
      "Understanding of harmony principles",
      "Ability to identify different harmony types",
      "Skills for creating balanced color schemes",
    ],
  },
  {
    id: "basic-color-schemes",
    title: "Basic Color Schemes",
    description:
      "Learn the difference between color harmony and color schemes, and create practical schemes for different purposes.",
    duration: "55",
    difficulty: "beginner",
    category: "Harmony",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["understanding-color-harmony"],
    objectives: [
      "Understand harmony vs. schemes",
      "Create monochromatic and analogous schemes",
      "Apply complementary and split-complementary schemes",
      "Build triadic schemes for balance",
    ],
    outcomes: [
      "Ability to create practical color schemes",
      "Understanding of scheme applications",
      "Skills for design problem solving",
    ],
  },
  {
    id: "introduction-to-contrast",
    title: "Introduction to Contrast",
    description:
      "Understand contrast types, WCAG requirements, and how to create accessible, readable designs.",
    duration: "45",
    difficulty: "beginner",
    category: "Accessibility",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["color-basics"],
    objectives: [
      "Define contrast and its importance",
      "Identify different contrast types",
      "Measure contrast ratios for accessibility",
      "Apply contrast principles to improve readability",
    ],
    outcomes: [
      "Understanding of contrast requirements",
      "Ability to create accessible designs",
      "Skills for WCAG compliance",
    ],
  },
  {
    id: "color-psychology-basics",
    title: "Color Psychology Basics",
    description:
      "Understand psychological impact of colors, how they affect emotions and behavior, and apply psychology to design decisions.",
    duration: "50",
    difficulty: "beginner",
    category: "Psychology",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["color-basics"],
    objectives: [
      "Understand emotional impact of colors",
      "Learn cultural color significance",
      "Explore color in marketing",
      "Study color and user behavior",
    ],
    outcomes: [
      "Ability to choose colors for emotional impact",
      "Understanding of cultural color considerations",
      "Skills for effective color marketing",
    ],
  },
  {
    id: "digital-vs-print-color",
    title: "Digital vs Print Color",
    description:
      "Understand fundamental differences between digital and print color, color models, and how to choose appropriate workflows.",
    duration: "45",
    difficulty: "beginner",
    category: "Theory",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["color-basics"],
    objectives: [
      "Understand digital vs. print color differences",
      "Identify RGB and CMYK color models",
      "Convert colors between color spaces",
      "Choose appropriate workflows for projects",
    ],
    outcomes: [
      "Understanding of color model differences",
      "Ability to convert between color spaces",
      "Skills for professional color workflows",
    ],
  },
  {
    id: "accessibility-fundamentals",
    title: "Accessibility Fundamentals",
    description:
      "Learn importance of accessibility, WCAG guidelines, and how to create color designs that work for all users.",
    duration: "55",
    difficulty: "beginner",
    category: "Accessibility",

    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["color-basics", "harmonies"],
    objectives: [
      "Understand WCAG color requirements",
      "Calculate contrast ratios",
      "Create accessible color palettes",
      "Test color accessibility",
    ],
    outcomes: [
      "Compliance with accessibility standards",
      "Ability to design for inclusive audiences",
      "Skills for accessible color selection",
    ],
  },
  {
    id: "advanced-techniques",
    title: "Advanced Color Techniques",
    description:
      "Master professional color workflows, digital color management, and advanced color theory applications.",
    duration: "90",
    difficulty: "advanced",
    category: "Application",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: [
      "basic-color-schemes",
      "accessibility-fundamentals",
      "color-psychology-basics",
    ],
    objectives: [
      "Advanced color mixing techniques",
      "Digital color space management",
      "Professional color workflows",
      "Color for different media",
    ],
    outcomes: [
      "Professional color management skills",
      "Understanding of color spaces",
      "Ability to work across different media",
    ],
  },
  // Additional mock lessons for comprehensive testing
  {
    id: "contrast-mastery",
    title: "Contrast Optimization Mastery",
    description:
      "Advanced techniques for optimizing contrast ratios while maintaining design aesthetics and brand identity.",
    duration: "65",
    difficulty: "advanced",
    category: "Application",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["accessibility-fundamentals"],
    objectives: [
      "Advanced contrast calculation",
      "Balancing aesthetics and accessibility",
      "Brand color optimization",
      "Contrast testing workflows",
    ],
    outcomes: [
      "Expert contrast optimization",
      "Brand-compliant accessible designs",
      "Professional testing workflows",
    ],
  },
  {
    id: "color-systems",
    title: "Color System Design",
    description:
      "Learn to design comprehensive color systems for brands, products, and digital platforms.",
    duration: "80",
    difficulty: "advanced",
    category: "Application",
    completed: false,
    locked: false,
    progress: 0,
    prerequisites: ["basic-color-schemes", "advanced-techniques"],
    objectives: [
      "Design system color principles",
      "Scalable color architecture",
      "Color token systems",
      "Cross-platform consistency",
    ],
    outcomes: [
      "Ability to design color systems",
      "Understanding of design tokens",
      "Skills for large-scale color management",
    ],
  },
];

// Helper function to get lessons by difficulty
export const getLessonsByDifficulty = (
  difficulty: "beginner" | "intermediate" | "advanced",
) => {
  return mockLessons.filter((lesson) => lesson.difficulty === difficulty);
};

// Helper function to get lessons by category
export const getLessonsByCategory = (category: string) => {
  return mockLessons.filter((lesson) => lesson.category === category);
};

// Helper function to get lesson by ID
export const getLessonById = (id: string) => {
  return mockLessons.find((lesson) => lesson.id === id);
};
